export interface OrganisationDetails {
  organisationId: string;
  tenantId: string;
  legalName: string;
  tradingName: string;
  abn: string;
  acn: string;
  isStpReporter: boolean;
  regionCode: string;
  defaultCurrency: string;
  defaultTimezone: string;
  primaryContact: {
    name: string;
    role: string;
    email: string;
    phone: string;
  };
  registeredBusinessAddress: {
    street: string;
    suburb: string;
    state: string;
    postcode: string;
    country: string;
  };
  status: 'Active' | 'Terminated';
  effectiveAt: string;
  retroactiveReason: string;
  defaultLogoImageId: string;
  brandPrimaryHex: string;
  brandAccentHex: string;
  brandSecondaryHex: string;
  updatedAt: string;
  updatedByUserId: string;
}

export interface OrganisationPermissions {
  canEdit: boolean;
  canEditRestrictedStatus: boolean;
  canView: boolean;
}

export interface OrganisationDetailsState {
  details: OrganisationDetails;
  permissions: OrganisationPermissions;
}

export type OrganisationFieldError = Partial<Record<keyof OrganisationDetails | 'primaryContact.email' | 'registeredBusinessAddress.postcode', string>>;

export const organisationDetailsFixture: OrganisationDetails = {
  organisationId: '7fb16ed2-8a67-45aa-bb5f-3fcb468bc718',
  tenantId: 'tenant-acme-au',
  legalName: 'Acme Holdings Pty Ltd',
  tradingName: 'Acme Group',
  abn: '12345678901',
  acn: '123456789',
  isStpReporter: false,
  regionCode: 'AU',
  defaultCurrency: 'AUD',
  defaultTimezone: 'Australia/Sydney',
  primaryContact: {
    name: 'Mark Williams',
    role: 'Group HR Director',
    email: 'mark.williams@example.com',
    phone: '+61 2 5550 0100',
  },
  registeredBusinessAddress: {
    street: '100 Market Street',
    suburb: 'Sydney',
    state: 'NSW',
    postcode: '2000',
    country: 'Australia',
  },
  status: 'Active',
  effectiveAt: '2026-06-05',
  retroactiveReason: '',
  defaultLogoImageId: '',
  brandPrimaryHex: '',
  brandAccentHex: '',
  brandSecondaryHex: '',
  updatedAt: '2026-06-04T13:00:00Z',
  updatedByUserId: '712020:e412e2a4-6d42-4b3d-a902-1d16c37cffcc',
};

export const validateOrganisationDetails = (details: OrganisationDetails): OrganisationFieldError => {
  const errors: OrganisationFieldError = {};
  const hexPattern = /^#[0-9a-fA-F]{6}$/;

  if (details.isStpReporter && details.abn.trim().length === 0)
    errors.abn = 'Enter an active ABR-validated ABN before enabling STP reporter.';

  if (details.abn && !/^\d{11}$/.test(details.abn))
    errors.abn = 'ABN must be 11 digits and pass backend checksum and ABR validation.';

  if (details.acn && !/^\d{9}$/.test(details.acn))
    errors.acn = 'ACN must be 9 digits and pass backend checksum validation.';

  for (const field of ['brandPrimaryHex', 'brandAccentHex', 'brandSecondaryHex'] as const) {
    if (details[field] && !hexPattern.test(details[field]))
      errors[field] = 'Use #RRGGBB format, for example #1E4D8B.';
  }

  if (details.effectiveAt < '2026-06-05' && details.retroactiveReason.trim().length === 0)
    errors.retroactiveReason = 'Retroactive changes require a reason for audit review.';

  return errors;
};
